/* eslint-disable max-len */
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import RemoveModal from '../../components/RemoveChartModal';
import Button from '../../components/Button/Button';
import ActionText from '../../components/ActionText/ActionText';
import Loader from '../../components/Loader';
import useRefreshTimeout from '../../helpers/refreshTimeout';
import ConnectedChart, { calculateNumberOfPoints } from '../../containers/ConnectedChart/ConnectedChart';
import { 
  activeDashboardRequest, 
  updateDashboardRequest,
  fetchDashboardDataRequest,
} from '../../store/dashboards/actions';
import { isRoleAction } from '../../helpers/hocs';

import * as shared from '../../styles/shared.module.scss';

const MemoizedChart = memo(ConnectedChart);

function DashboardsList({ getDashboard, dashboardParent, activeProject, updateDashboard, fetchDashboardData }) {
  const { addItemToGroup, removeItemFromGroup, moveItemBetweenGroups } = useRefreshTimeout();
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [internalDashboards, setInternalDashboards] = useState([]);
  const [activeModel, setActiveModel] = useState({});
  const [removeModalOpened, setRemoveModalOpened] = useState(false);
  const navigate = useNavigate();
  const { shortName } = useParams();
  const ref = useRef({});
  const chartAddedToTheGroup = useRef(false);

  const moveItems = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return;

    const updatedDashboards = [...internalDashboards];
    const [draggedItem] = updatedDashboards.splice(dragIndex, 1);
    updatedDashboards.splice(hoverIndex, 0, draggedItem);

    setInternalDashboards(updatedDashboards);
  }, [internalDashboards]);

  const triggerSave = () => {
    const updatedDashboards = internalDashboards.map((dashboard, index) => ({
      ...dashboard,
      order: index,
    }));

    updateDashboard({ ...dashboardParent, charts: updatedDashboards });
  };

  const labelMatcher = {
    '1 Day': '1d',
    '1 Week': '1w',
    '2 Weeks': '2w',
    '1 Month': '1M',
  };

  const intervalToShortValue = {
    'Minute': '1m',
    'Hour': '1h',
    'Day': '1d',
    'Month': '1M',
  };

  const changeRange = useCallback((model, id, range, startDate, endDate) => {
    if (!ref.current[id]) {
      ref.current[id] = { range, startDate, endDate };
    } else if (ref.current[id] && ref.current[id]?.range !== range) {
      ref.current[id] = { range, startDate, endDate };

      const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;
      const offset = startDate.utcOffset();

      let interval = model?.interval;

      if (model?.interval === '1h' || model?.interval === '1m') {
        const { durationInMinutes, durationInHours } = calculateNumberOfPoints(newRange);
  
          // First check if the number of points in minutes exceeds the threshold
          if (durationInMinutes > 744) {
            interval = '1h';
          }

          // Then check if the number of points in hours exceeds the threshold
          if (durationInHours > 744) {
            interval = '1d';
          }
      }

      const updatedCharts = dashboardParent.charts.map(chart => {
        if (chart.id === id) {
          return { ...chart, range: newRange, interval };
        }
        return chart;
      });

      updateDashboard({ ...dashboardParent, charts: updatedCharts });
      fetchDashboardData({
        charts: [{
          id,
          range: newRange,
          interval,
          timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
        }]
      });
    }
  }, [dashboardParent, updateDashboard, fetchDashboardData]);

  useEffect(() => {
    if (dashboardParent?.charts && !chartAddedToTheGroup.current) {
      dashboardParent.charts.forEach(chart => {
        addItemToGroup(chart, chart.refresh);
      });
      chartAddedToTheGroup.current = true;
    }
  }, [dashboardParent, addItemToGroup]);

  useEffect(() => {
    if (dashboardParent?.charts) {
      setInternalDashboards(dashboardParent.charts);
    }
  }, [dashboardParent, addItemToGroup]);

  useEffect(() => {
    setLoading(true);
    getDashboard({ id: activeProject.id }, () => {
      setLoading(false);
    });
  }, [activeProject?.id]);

  const create = useCallback(() => navigate(`/${shortName}/dashboards/create`), [navigate, shortName]);

  const handleClickOptions = useCallback((key, model) => {
    if (key === 'delete') {
      setActiveModel(model);
      setRemoveModalOpened(true);
      removeItemFromGroup(model, model.refresh);
    } else if (key === 'edit') {
      navigate(`/${shortName}/dashboards/${model.id}?refresh=${model.refresh}&type=${model.type}`);
    }
  }, [navigate, shortName]);

  const onClickChartOption = useCallback((model, key, value) => {
    if (key === 'refresh') {
      const updatedCharts = dashboardParent.charts.map(chart => {
        if (chart.id === model.id) {
          return { ...chart, refresh: value };
        }
        return chart;
      });

      updateDashboard({ ...dashboardParent, charts: updatedCharts }, () => {
        moveItemBetweenGroups(model, model.refresh, value);
      });
    } else if (key === 'chartType') {
      const updatedCharts = dashboardParent.charts.map(chart => {
        if (chart.id === model.id) {
          return { ...chart, type: value };
        }
        return chart;
      });

      updateDashboard({ ...dashboardParent, charts: updatedCharts });
    } else if (key === 'interval') {

      const { startDate } = ref.current[model.id] || {};
      const offset = startDate.utcOffset();
      const updatedCharts = dashboardParent.charts.map(chart => {
        if (chart.id === model.id) {
          return { ...chart, interval: value };
        }
        return chart;
      });

      updateDashboard({ ...dashboardParent, charts: updatedCharts });
      fetchDashboardData({
        charts: [{
          id: model.id,
          range: model.range,
          interval: value,
          timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
        }]
      });
    }
  }, [dashboardParent, updateDashboard]);

  return (
    <div className={shared.page}>
      <div className={shared.header}>
        <div className={shared.headerTitleGroup}>
          <h1 className={shared.headerTitle}>Dashboard</h1>
        </div>
        <div className={shared.headerButtonGroup}>
          <Button title="Create chart" onClick={create} />
        </div>
      </div>
      <div className={shared.table} ref={scrollRef} style={{
        borderTop: '1px solid #E8E9ED',
        display: 'block',
        overflow: 'auto',
        height: 'calc(100vh - 86px)',
      }}>
        {(internalDashboards?.length && !loading) ? (
          <tbody style={{ 
            overflowY: 'auto', 
            display: 'flex',
            flexWrap: 'wrap',
            position: 'relative',
            padding: '20px',
            gap: '20px',
            marginBottom: '60px'
          }}>
            <DndProvider backend={HTML5Backend}>
              {internalDashboards.map((model, i) => (
                <MemoizedChart
                  key={model.id}
                  index={i}
                  id={model.id}
                  isCube
                  title={model.name}
                  description={model.description}
                  chartType={model.type}
                  interval={model.interval}
                  groups={model.groups || ['2XX', '3XX', '4XX', '5XX']}
                  refresh={model.refresh}
                  initValue={model.range}
                  model={model}
                  handleClickOptions={handleClickOptions}
                  handleClickSelectOption={(...options) => onClickChartOption(model, ...options)}
                  moveItems={moveItems}
                  triggerSaveRange={(...options) => changeRange(model, ...options)}
                  triggerSave={triggerSave}
                  scrollRef={scrollRef}
                />
              ))}
            </DndProvider>
          </tbody>
        ) : null}
        {loading && (
          <div className={shared.emptyContent}>
            <Loader size="large" />
          </div>
        )}
        {!internalDashboards?.length && !loading && (
          <div className={shared.emptyContent} style={{ marginTop: 204 }}>
            <h3 className={shared.caption}>This project has no Dashboards yet</h3>
            <p>
              The available Dashboards for this project will be displayed here.
            </p>
            <div className={shared.action}>
              <ActionText
                title="Click here to create Dashboard"
                onClick={create}
              />
            </div>
          </div>
        )}
        {removeModalOpened && (
          <RemoveModal
            onClose={() => setRemoveModalOpened(false)}
            onSubmit={() => {
              setRemoveModalOpened(false);
            }}
            model={activeModel}
            activeProject={activeProject}
          />
        )}
      </div>
    </div>
  );
}

const mapStateToProps = ({ dashboard, auth: { currentUser }, activeProject, roles }) => ({
  dashboardParent: dashboard?.data,
  roles,
  currentUser,
  activeProject
});

const mapDispatchToProps = (dispatch) => ({
  getDashboard: (data, callback) => dispatch(activeDashboardRequest(data, callback)),
  updateDashboard: (data, callback) => dispatch(updateDashboardRequest(data, callback)),
  fetchDashboardData: (data, callback) => dispatch(fetchDashboardDataRequest(data, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(isRoleAction(DashboardsList));